import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Users,
  FileText,
  PlayCircle,
  Target
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { DashboardSkeleton } from "@/components/ui/skeleton-card";
import { ErrorState } from "@/components/ui/empty-state";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from "recharts";
import { format, subDays, startOfDay, endOfDay } from "date-fns";

interface OutletContext {
  user: any;
  organizationId: string;
}

interface AnalyticsData {
  totalRuns: number;
  completedRuns: number;
  inProgressRuns: number;
  cancelledRuns: number;
  totalSOPs: number;
  activeSOPs: number;
  totalSteps: number;
  completedSteps: number;
  skippedSteps: number;
  overdueSteps: number;
  avgCompletionRate: number;
  onTimeRate: number;
  runsByDay: { date: string; completed: number; started: number }[];
  sopUsage: { name: string; runs: number; color: string }[];
  stepStatusBreakdown: { name: string; value: number; color: string }[];
}

const COLORS = ['hsl(245, 58%, 60%)', 'hsl(45, 93%, 58%)', 'hsl(217, 91%, 60%)', 'hsl(142, 71%, 45%)', 'hsl(0, 84%, 60%)'];

const Analytics = () => {
  const { organizationId } = useOutletContext<OutletContext>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalyticsData>({
    totalRuns: 0,
    completedRuns: 0,
    inProgressRuns: 0,
    cancelledRuns: 0,
    totalSOPs: 0,
    activeSOPs: 0,
    totalSteps: 0,
    completedSteps: 0,
    skippedSteps: 0,
    overdueSteps: 0,
    avgCompletionRate: 0,
    onTimeRate: 0,
    runsByDay: [],
    sopUsage: [],
    stepStatusBreakdown: [],
  });

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch runs data
      const { data: runsData, error: runsError } = await supabase
        .from("sop_runs")
        .select("*, sop_template:sop_templates(title)")
        .eq("organization_id", organizationId);

      if (runsError) throw runsError;

      // Fetch SOPs data
      const { data: sopsData, error: sopsError } = await supabase
        .from("sop_templates")
        .select("*")
        .eq("organization_id", organizationId);

      if (sopsError) throw sopsError;

      // Fetch run steps data
      const runIds = runsData?.map(r => r.id) || [];
      let stepsData: any[] = [];
      
      if (runIds.length > 0) {
        const { data: steps, error: stepsError } = await supabase
          .from("sop_run_steps")
          .select("*")
          .in("sop_run_id", runIds);
        
        if (stepsError) throw stepsError;
        stepsData = steps || [];
      }

      // Calculate metrics
      const runs = runsData || [];
      const sops = sopsData || [];
      const steps = stepsData;

      const completedRuns = runs.filter(r => r.status === "COMPLETED").length;
      const inProgressRuns = runs.filter(r => r.status === "IN_PROGRESS").length;
      const cancelledRuns = runs.filter(r => r.status === "CANCELLED").length;

      const completedSteps = steps.filter(s => s.status === "COMPLETED").length;
      const skippedSteps = steps.filter(s => s.status === "SKIPPED").length;
      const overdueSteps = steps.filter(s => 
        s.due_at && new Date(s.due_at) < new Date() && 
        !["COMPLETED", "SKIPPED"].includes(s.status)
      ).length;

      // Runs by day (last 7 days)
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = subDays(new Date(), 6 - i);
        const dayStart = startOfDay(date);
        const dayEnd = endOfDay(date);
        
        const started = runs.filter(r => {
          const createdAt = new Date(r.created_at);
          return createdAt >= dayStart && createdAt <= dayEnd;
        }).length;

        const completed = runs.filter(r => {
          if (!r.completed_at) return false;
          const completedAt = new Date(r.completed_at);
          return completedAt >= dayStart && completedAt <= dayEnd;
        }).length;

        return {
          date: format(date, "MMM d"),
          started,
          completed,
        };
      });

      // SOP usage breakdown
      const sopUsageMap = new Map<string, number>();
      runs.forEach(run => {
        const title = run.sop_template?.title || "Unknown";
        sopUsageMap.set(title, (sopUsageMap.get(title) || 0) + 1);
      });
      
      const sopUsage = Array.from(sopUsageMap.entries())
        .map(([name, runs], index) => ({
          name: name.length > 20 ? name.substring(0, 20) + "..." : name,
          runs,
          color: COLORS[index % COLORS.length],
        }))
        .sort((a, b) => b.runs - a.runs)
        .slice(0, 5);

      // Step status breakdown
      const stepStatusBreakdown = [
        { name: "Completed", value: completedSteps, color: "hsl(142, 71%, 45%)" },
        { name: "In Progress", value: steps.filter(s => s.status === "IN_PROGRESS").length, color: "hsl(217, 91%, 60%)" },
        { name: "Not Started", value: steps.filter(s => s.status === "NOT_STARTED").length, color: "hsl(220, 9%, 46%)" },
        { name: "Skipped", value: skippedSteps, color: "hsl(45, 93%, 58%)" },
      ].filter(s => s.value > 0);

      // Calculate rates
      const totalActiveRuns = runs.filter(r => r.status !== "CANCELLED").length;
      const avgCompletionRate = totalActiveRuns > 0 
        ? Math.round((completedRuns / totalActiveRuns) * 100) 
        : 0;

      // On-time rate calculation (runs completed before or on due date)
      const runsWithDue = runs.filter(r => r.due_at && r.status === "COMPLETED");
      const onTimeRuns = runsWithDue.filter(r => 
        new Date(r.completed_at!) <= new Date(r.due_at!)
      ).length;
      const onTimeRate = runsWithDue.length > 0 
        ? Math.round((onTimeRuns / runsWithDue.length) * 100) 
        : 0;

      setData({
        totalRuns: runs.length,
        completedRuns,
        inProgressRuns,
        cancelledRuns,
        totalSOPs: sops.length,
        activeSOPs: sops.filter(s => s.status === "ACTIVE").length,
        totalSteps: steps.length,
        completedSteps,
        skippedSteps,
        overdueSteps,
        avgCompletionRate,
        onTimeRate,
        runsByDay: last7Days,
        sopUsage,
        stepStatusBreakdown,
      });
    } catch (err: any) {
      console.error("Analytics fetch error:", err);
      setError(err.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [organizationId]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="container py-8 px-4">
        <ErrorState 
          title="Failed to load analytics" 
          description={error}
          onRetry={fetchAnalytics}
        />
      </div>
    );
  }

  const statCards = [
    { 
      title: "Completion Rate", 
      value: `${data.avgCompletionRate}%`, 
      icon: Target, 
      color: "text-accent",
      bgColor: "bg-accent/10",
      description: "Runs completed vs started"
    },
    { 
      title: "On-Time Rate", 
      value: data.onTimeRate > 0 ? `${data.onTimeRate}%` : "--", 
      icon: Clock, 
      color: "text-primary",
      bgColor: "bg-primary/10",
      description: "Completed before due date"
    },
    { 
      title: "Completed Runs", 
      value: data.completedRuns, 
      icon: CheckCircle, 
      color: "text-success",
      bgColor: "bg-success/10",
      description: "All-time completed"
    },
    { 
      title: "Overdue Steps", 
      value: data.overdueSteps, 
      icon: AlertCircle, 
      color: data.overdueSteps > 0 ? "text-destructive" : "text-muted-foreground",
      bgColor: data.overdueSteps > 0 ? "bg-destructive/10" : "bg-muted",
      description: "Needs attention"
    },
  ];

  return (
    <div className="container py-4 sm:py-6 md:py-8 space-y-4 sm:space-y-6 px-4 sm:px-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">Analytics</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Track performance across your operations
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, i) => (
          <Card key={i} className="relative overflow-hidden">
            <div className={`absolute inset-0 ${stat.bgColor} opacity-30`} />
            <CardHeader className="relative flex flex-row items-center justify-between pb-2 p-3 sm:p-6 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent className="relative p-3 pt-0 sm:p-6 sm:pt-0">
              <div className="text-2xl sm:text-3xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
        {/* Runs Over Time */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Runs Over Time
            </CardTitle>
            <CardDescription className="text-sm">
              Last 7 days activity
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {data.runsByDay.some(d => d.started > 0 || d.completed > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.runsByDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      color: 'hsl(var(--foreground))'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="started" name="Started" fill="hsl(217, 91%, 60%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed" name="Completed" fill="hsl(142, 71%, 45%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Start some runs to see trends</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step Status Breakdown */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-accent" />
              Step Status Breakdown
            </CardTitle>
            <CardDescription className="text-sm">
              Current status of all steps
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {data.stepStatusBreakdown.length > 0 ? (
              <div className="flex items-center justify-center gap-8">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={data.stepStatusBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {data.stepStatusBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        color: 'hsl(var(--foreground))'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {data.stepStatusBreakdown.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm">{item.name}</span>
                      <Badge variant="secondary" className="ml-auto">
                        {item.value}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <CheckCircle className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No steps data yet</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
        {/* SOP Usage */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Top SOPs by Usage
            </CardTitle>
            <CardDescription className="text-sm">
              Most frequently run procedures
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {data.sopUsage.length > 0 ? (
              <div className="space-y-4">
                {data.sopUsage.map((sop, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate max-w-[60%]">
                        {sop.name}
                      </span>
                      <Badge variant="outline">{sop.runs} runs</Badge>
                    </div>
                    <Progress 
                      value={(sop.runs / Math.max(...data.sopUsage.map(s => s.runs))) * 100} 
                      className="h-2"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Run some SOPs to see usage data</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-accent" />
              Summary
            </CardTitle>
            <CardDescription className="text-sm">
              Overall operations overview
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1 p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  <span className="text-xs">Total SOPs</span>
                </div>
                <p className="text-2xl font-bold">{data.totalSOPs}</p>
                <p className="text-xs text-muted-foreground">{data.activeSOPs} active</p>
              </div>
              <div className="space-y-1 p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <PlayCircle className="h-4 w-4" />
                  <span className="text-xs">Total Runs</span>
                </div>
                <p className="text-2xl font-bold">{data.totalRuns}</p>
                <p className="text-xs text-muted-foreground">{data.inProgressRuns} in progress</p>
              </div>
              <div className="space-y-1 p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-xs">Steps Completed</span>
                </div>
                <p className="text-2xl font-bold">{data.completedSteps}</p>
                <p className="text-xs text-muted-foreground">of {data.totalSteps} total</p>
              </div>
              <div className="space-y-1 p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span className="text-xs">Skip Rate</span>
                </div>
                <p className="text-2xl font-bold">
                  {data.totalSteps > 0 
                    ? `${Math.round((data.skippedSteps / data.totalSteps) * 100)}%` 
                    : "--"}
                </p>
                <p className="text-xs text-muted-foreground">{data.skippedSteps} skipped</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;